import { Component, ChangeDetectionStrategy, ElementRef, OnInit, OnDestroy, ViewChild } from '@angular/core';

interface HomeCategory { name: string; slug: string; description: string; image: string; available: boolean; }
interface WhyUsItem { image: string; title: string; description: string; serial: string; }

@Component({
  selector: 'app-home', templateUrl: './home.component.html', styleUrls: ['./home.component.css'],
  changeDetection: ChangeDetectionStrategy.Eager, standalone: false
})
export class HomeComponent implements OnInit, OnDestroy {
  @ViewChild('videoBox') videoBox!: ElementRef<HTMLElement>;
  private player: any;
  private pendingIframe: HTMLIFrameElement | null = null;
  activeTestimonial = 0;

  previousTestimonial(): void { this.activeTestimonial = (this.activeTestimonial - 1 + this.testimonials.length) % this.testimonials.length; }
  nextTestimonial(): void { this.activeTestimonial = (this.activeTestimonial + 1) % this.testimonials.length; }

  categories: HomeCategory[] = [
    { name: 'Yoga', slug: 'yoga', description: 'Movement, expression, balance and stillness captured through light and form.', image: '/assets/photography/yoga/yoga-08.webp', available: true },
    { name: 'Wedding', slug: 'wedding', description: 'Natural, emotional storytelling that preserves the people and moments that matter.', image: '/assets/photography/wedding/wedding-05.webp', available: true },
  ];

  selectedWork = [
    '/assets/photography/wedding/wedding-08.webp',
    '/assets/photography/yoga/yoga-06.webp',
    '/assets/photography/wedding/wedding-07.webp',
    '/assets/photography/yoga/yoga-10.webp'
  ];

  whyUs: WhyUsItem[] = [
    { image: '/assets/images/why-us/1.png', title: 'LIGHT COMPOSURE', serial: '01', description: 'Light is a dynamic element that you have at your disposal when capturing a photograph; composition is how you choose to combine various elements to tell a story.' },
    { image: '/assets/images/why-us/2.png', title: 'PROFESSIONAL SKILLS', serial: '02', description: 'Captures moments on film and uses images to convey stories. They collaborate with art teams, develop concepts, and employ various techniques to achieve desired results.' },
    { image: '/assets/images/why-us/3.png', title: 'PERFECT EQUIPMENT', serial: '03', description: 'Capturing the perfect photograph requires more than just a camera. A tripod to keep your camera steady. A reflector to bounce light back into your subject’s face.' },
    { image: '/assets/images/why-us/4.png', title: 'ULTRA HD', serial: '04', description: 'Our photographs easily exceed resolutions beyond 6,000 x 4,000 pixels at 300 dpi. This allows for extremely large high quality outdoor prints.' },
    { image: '/assets/images/why-us/5.png', title: 'VISION', serial: '05', description: 'Being able to think creatively and attentively at a subject and capture it in a way that conveys meaning. It is abstract and expressive.' },
    { image: '/assets/images/why-us/6.png', title: 'FOCUSING KNOWLEDGES', serial: '06', description: 'We work best with the lens to highlight a person or a situation. Focus is one of the pillars of photography, along with ISO, aperture, and shutter speed.' }
  ];

  experience = {
    title: 'Experience',
    items: [{ count: '15', title: 'Years of Experience' }]
  };

  testimonials = [
    {
      name: 'Cheryl Ling',
      quote: '“It was a very pleasant photoshooting experience with Vishwas. We met to discuss the details of the shoot before the actual shooting day. He understands what his clients are asking for. Being a local himself, he is familiar with the best places to shoot around Mysore. He is an attentive and a passionate photographer who will definitely meets your expectation. Will definitely engage him as my photographer in the coming future! Loving all my pictures taken by him, which have received many compliments from my friends.”',
      link: 'https://g.co/kgs/LzfwpE'
    },
    {
      name: 'IngerYoga',
      quote: '“The photoshoot with Vishwas was great. It was efficient, with high quality photos. Photoshoot had a great price and photographer gives you all the photos he took. Vishwas is a really nice guy and you will surely enjoy photoshooting with him. I reccomend it.”',
      link: 'https://g.co/kgs/QxixAs'
    }
  ];

  private onFullscreenChange = (): void => {
    if (!document.fullscreenElement) {
      this.videoBox?.nativeElement.querySelector('iframe.youtube-player')?.remove();
      this.player = null;
    }
  };

  private openFullscreen(iframe: HTMLIFrameElement): void {
    if (document.fullscreenElement) return;
    iframe.requestFullscreen?.().catch(() => {});
  }

  private isSafari(): boolean { return /^((?!chrome|android).)*safari/i.test(navigator.userAgent); }

  private setUpPlayer(iframe: HTMLIFrameElement): void {
    const YT = (window as any).YT;
    try {
      this.player = new YT.Player(iframe, {
        videoId: iframe.dataset['videoId'], playerVars: { autoplay: 1, rel: 0, playsinline: 1 },
        events: {
          onReady: () => { this.openFullscreen(iframe); this.player?.playVideo(); },
          onStateChange: (state: any) => { if (state?.data === 1) this.openFullscreen(iframe); }
        }
      });
    } catch { this.openFullscreen(iframe); }
  }

  playVideo(event: Event, videoId: string): void {
    event.preventDefault();
    const box = this.videoBox.nativeElement;
    let iframe = box.querySelector<HTMLIFrameElement>('iframe.youtube-player');
    if (!iframe) {
      iframe = document.createElement('iframe');
      iframe.className = 'youtube-player';
      iframe.dataset['videoId'] = videoId;
      iframe.src = `https://www.youtube-nocookie.com/embed/${videoId}?autoplay=1&rel=0&playsinline=1&enablejsapi=1&origin=${encodeURIComponent(window.location.origin)}`;
      iframe.title = 'Journey of Laruga — Vishwas Fotography';
      iframe.allow = 'accelerometer; autoplay; clipboard-write; encrypted-media; fullscreen; gyroscope; picture-in-picture; web-share';
      iframe.allowFullscreen = true;
      iframe.style.cssText = 'position:absolute;top:0;left:0;width:100%;height:100%;border:0;';
      box.appendChild(iframe);
    }
    if (this.isSafari()) this.openFullscreen(iframe);
    if ((window as any).YT?.Player) this.setUpPlayer(iframe);
    else {
      this.pendingIframe = iframe;
      (window as any).onYouTubeIframeAPIReady = () => { if (this.pendingIframe) { this.setUpPlayer(this.pendingIframe); this.pendingIframe = null; } };
      if (!document.getElementById('youtube-iframe-api')) {
        const script = document.createElement('script');
        script.id = 'youtube-iframe-api';
        script.src = 'https://www.youtube.com/iframe_api';
        script.async = true;
        document.body.appendChild(script);
      }
    }
  }

  ngOnInit(): void {
    document.addEventListener('fullscreenchange', this.onFullscreenChange);
  }
  ngOnDestroy(): void { document.removeEventListener('fullscreenchange', this.onFullscreenChange); }
}
